/* eslint-disable @typescript-eslint/no-unused-vars */
'use client'

import { useDisconnect } from '@reown/appkit/react'
import { useAtom } from 'jotai'
import { useSetAtom } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import { PropsWithChildren, useEffect, useRef } from 'react'

import { message } from '@/components'
import { BSC_CHAIN } from '@/consts'
import { modal } from '@/contexts'
import { useAppkitInit } from '@/hooks/use-appkit-init'
import { useExternalDisconnect } from '@/hooks/use-external-disconnect'
import { useSignMessage } from '@/hooks/use-sign-message'
import { useAccountEffect } from '@/hooks/use-wallet-success'
import { atomLoginStatus, LoginStatus, resetStateAtom } from '@/stores/login'
import { decloudTokenAtom, gataTokenAtom, userAddressAtom, useUserStore } from '@/stores/user'
import { setToken } from '@/utils/auth'
import { emitter } from '@/utils/eventBus'
import { authNonce, getNonce, grantToken } from '@/utils/siwe'

const TOKEN_EXPIRY_MS = 24 * 60 * 60 * 1000 // 24 hours
const REFRESH_BEFORE_MS = 30 * 60 * 1000 // Refresh if token expires within 30 mins
const CHECK_INTERVAL_MS = 10 * 60 * 1000 // Check every 10 minutes

const LoginContext: React.FC<PropsWithChildren> = ({ children }) => {
  const { signMessage } = useSignMessage()

  const [_, setLoginStatus] = useAtom(atomLoginStatus)

  const setUserAddress = useSetAtom(userAddressAtom)
  const setGataToken = useSetAtom(gataTokenAtom)
  const setDeCloudToken = useSetAtom(decloudTokenAtom)

  const resetState = useResetAtom(resetStateAtom)

  const { disconnect } = useDisconnect()
  const userStore = useUserStore()

  const tokenFetchedAtRef = useRef<number | null>(null)
  const isRefreshingRef = useRef<boolean>(false)

  useAppkitInit()

  useExternalDisconnect()

  const fetchDeCloudToken = async () => {
    try {
      const res: { token: string; code: number } | undefined = await grantToken(4)
      if (res?.code === 0) {
        const now = Date.now()
        tokenFetchedAtRef.current = now
        saveTokenFetchedAt(now)

        setDeCloudToken(res.token)
        setToken(`Bearer ${res.token}`, true)
      } else {
        console.error('[fetchDeCloudToken] code error:', res)
        throw Error('Fialed to login')
      }
    } catch (e) {
      console.error('[fetchDeCloudToken]', e)
      throw e
    }
  }

  const onLogout = ({ reason }: { reason: string }) => {
    console.log('[onLogout]', { reason })
    userStore.logout()
    resetState()
  }

  useEffect(() => {
    emitter.on('LOGOUT', onLogout)

    return () => {
      emitter.off('LOGOUT')
    }
  }, [])

  useAccountEffect({
    onSuccess: async (eip55Address: string) => {
      try {
        await modal.switchNetwork(BSC_CHAIN)
        const nonce = await getNonce(eip55Address as string)
        const signature = await signMessage({ message: nonce.auth_nonce })
        const a = await authNonce(eip55Address, signature)
        if (a.code === 0) {
          setUserAddress(eip55Address)
          setGataToken(a.token)
          await fetchDeCloudToken()

          try {
            await Promise.all([
              userStore.getUserInfo(),
              userStore.getAccount(),
              userStore.getVolume(),
              userStore.getSshPubKey(),
            ])
          } catch (error) {
            console.error('Fetch onLogin error', error)
          }

          setLoginStatus(LoginStatus.login)

          modal.close()
        }
      } catch (error: any) {
        console.log('signMessageError', error)
        setUserAddress('')
        setGataToken('')
        message.error(error.message)
        disconnect()
      }
    },
  })

  const loadTokenFetchedAt = (): number | null => {
    const saved = localStorage.getItem('tokenFetchedAt')
    return saved ? parseInt(saved) : null
  }

  const saveTokenFetchedAt = (timestamp: number) => {
    localStorage.setItem('tokenFetchedAt', timestamp.toString())
  }

  useEffect(() => {
    // Try to load the persistent fetch time
    const savedFetchedAt = loadTokenFetchedAt()
    if (!savedFetchedAt) return // No token yet, do nothing

    const checkTokenExpiry = () => {
      if (isRefreshingRef.current) return

      const now = Date.now()
      const elapsed = now - savedFetchedAt
      const remaining = TOKEN_EXPIRY_MS - elapsed

      console.log(
        `[Token Checker] Token fetched at ${new Date(savedFetchedAt).toISOString()}, elapsed: ${elapsed}ms, remaining: ${remaining}ms`,
      )

      if (remaining <= REFRESH_BEFORE_MS && remaining > 0) {
        console.log('[Token Checker] Token will expire soon, refreshing...')
        isRefreshingRef.current = true

        fetchDeCloudToken()
          .then(() => {
            // Update the token fetch timestamp AFTER successful refresh
            tokenFetchedAtRef.current = Date.now()
            saveTokenFetchedAt(Date.now())
            console.log('[Token Checker] Token refreshed & timestamp updated')
          })
          .catch((err) => {
            console.error('[Token Checker] Failed to refresh token:', err)
          })
          .finally(() => {
            isRefreshingRef.current = false
          })
      } else if (remaining <= 0) {
        console.warn('[Token Checker] Token may have expired, please re-authenticate')
      }
    }

    // Run immediately
    checkTokenExpiry()

    // Set up interval (e.g. every 10 minutes)
    const interval = setInterval(checkTokenExpiry, CHECK_INTERVAL_MS)

    return () => clearInterval(interval)
  }, []) // Run once on mount

  return children
}

export default LoginContext
